import React from 'react';
import { Box, Card, CardContent, CardActionArea, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import GroupsIcon from '@mui/icons-material/Groups';
import CampaignIcon from '@mui/icons-material/Campaign';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import BoltIcon from '@mui/icons-material/Bolt';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import { useAuth } from '../contexts/AuthContext';

// Section order + headers. Each MANAGE_ITEM carries a `category` key that maps here, so adding a new
// Manage tool is just a matter of dropping it in the list with the right category — it slots into its
// section automatically. Empty sections (all items hidden or not permitted) don't render.
const CATEGORIES = [
  { key: 'access', label: 'Access & staff', icon: <GroupsIcon fontSize="small" /> },
  { key: 'comms', label: 'Communications', icon: <CampaignIcon fontSize="small" /> },
  { key: 'sponsors', label: 'Sponsorships & donors', icon: <VolunteerActivismIcon fontSize="small" /> },
  { key: 'facilities', label: 'Facilities & operations', icon: <BoltIcon fontSize="small" /> },
  { key: 'system', label: 'System & data', icon: <SettingsSuggestIcon fontSize="small" /> },
];

// Items to manage – add more entries here as needed (set `category` to one of CATEGORIES.key)
const MANAGE_ITEMS = [
  {
    path: '/manage/civi-migration-jobs',
    label: 'Civi migration jobs',
    description: 'Scheduled migration queue, run stats, logs, and reschedule',
    perms: ['reports:view'],
    category: 'system',
    hidden: true, // hidden from the Manage list (route/page kept); un-hide to restore
  },
  {
    path: '/manage/users',
    label: 'User management',
    description: 'Manage staff users and permissions',
    perms: ['users:manage'],
    category: 'access',
  },
  {
    path: '/manage/roles',
    label: 'Role management',
    description: 'Roles and default permissions',
    perms: ['users:manage'],
    category: 'access',
  },
  {
    path: '/manage/rosters',
    label: 'Rosters',
    description: 'View Trustees, Trustee-on-Duty, and Event Coordinator rosters by year',
    // Matches the backend ROSTER_PERM so anyone allowed to hit the roster APIs (incl. Coordinators
    // via communications:access) sees the card.
    perms: ['reports:view', 'event_reports:access', 'sponsors_reports:access', 'communications:access'],
    category: 'access',
  },
  {
    path: '/manage/email-templates',
    label: 'Email Templates',
    description: 'Add, edit, and delete reusable email templates',
    perms: ['reports:view'],
    category: 'comms',
  },
  {
    path: '/manage/calendar-events',
    label: 'Calendar Events & Newsletters',
    description: 'Manage events and sessions, map products, and schedule event newsletters',
    perms: ['reports:view', 'calendar:manage'],
    category: 'comms',
  },
  {
    path: '/manage/alerts-reminders',
    label: 'Alerts and reminders',
    description: 'Read-only audit log of automated reminder emails sent by the server',
    perms: ['reports:view'],
    category: 'comms',
  },
  {
    path: '/manage/kiosk-prime-products',
    label: 'Kiosk Prime Products',
    description: 'Manage promoted kiosk products with start and end dates',
    perms: ['reports:view'],
    category: 'system',
    hidden: true, // hidden from the Manage list (route/page kept); un-hide to restore
  },
  {
    path: '/manage/sponsor-benefits',
    label: 'Sponsor Benefits',
    description: 'Configure sponsor product/variation benefits linked to event reports',
    perms: ['reports:view'],
    category: 'sponsors',
  },
  {
    path: '/manage/life-membership-eligibility',
    label: 'Life-membership Eligibility',
    description: 'Donors who qualify for Life Membership by giving level; preview & send eligibility emails',
    perms: ['reports:view'],
    category: 'sponsors',
  },
  {
    path: '/manage/special-sponsorships',
    label: 'Special Sponsorships',
    description: 'Brahmotsavam Vahana donors — track sponsors and send recognition emails',
    perms: ['admin:full'],
    category: 'sponsors',
  },
  {
    path: '/temple/venkateswara-sahasranama-mala',
    label: 'Venkateswara Sahasranama Mala',
    description: 'Sahasranama Mala coin sponsors — records, engraving, and coin tracking',
    // Same audience it had in the Temple menu (hasEventReports)
    perms: ['reports:view', 'event_reports:access'],
    category: 'sponsors',
  },
  {
    path: '/manage/solar',
    label: 'Solar Monitoring',
    description: 'Campus solar fleet across APsystems, Sol-Ark, and EG4 — production, battery, and fault status',
    perms: ['reports:view'],
    category: 'facilities',
  },
  {
    path: '/manage/civi-wc-product-mapping',
    label: 'CiviCRM migration',
    description: 'Map CiviCRM financial types and contribution pages to WooCommerce products',
    perms: ['reports:view'],
    category: 'system',
    hidden: true, // hidden from the Manage list (route/page kept); un-hide to restore
  },
  {
    path: '/settings/sync',
    label: 'Data & sync',
    description: 'Manual catalog refresh from WooCommerce and sync status',
    perms: ['settings:access'],
    category: 'system',
  },
  {
    path: '/settings/system-log',
    label: 'System Log',
    description: 'Audit of key system actions',
    perms: ['settings:access'],
    category: 'system',
  },
];

function canAccess(perms, role, requiredPerms) {
  if (String(role || '').toLowerCase() === 'admin') return true;
  if (Array.isArray(perms) && perms.includes('admin:full')) return true;
  return requiredPerms.some((p) => perms.includes(p));
}

export default function ManagePage() {
  const navigate = useNavigate();
  const { staff } = useAuth();
  const perms = Array.isArray(staff?.permissions) ? staff.permissions : [];
  const visible = MANAGE_ITEMS.filter((item) => !item.hidden && canAccess(perms, staff?.role, item.perms || []));

  const renderCard = (item) => (
    <Card
      key={item.path}
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        height: '100%',
        '&:hover': { boxShadow: 2, borderColor: 'primary.main' },
      }}
    >
      <CardActionArea onClick={() => navigate(item.path)} sx={{ height: '100%' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, py: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {item.label}
            </Typography>
            {item.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {item.description}
              </Typography>
            )}
          </Box>
          <ChevronRightIcon color="action" fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
        </CardContent>
      </CardActionArea>
    </Card>
  );

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Manage</Typography>
        <Typography variant="body2" color="text.secondary">
          Special programs, people, and data views — grouped by area
        </Typography>
      </Box>

      {CATEGORIES.map((cat) => {
        const items = visible.filter((item) => item.category === cat.key);
        if (!items.length) return null;
        return (
          <Box key={cat.key} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: 'text.secondary' }}>
              {cat.icon}
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary' }}>
                {cat.label}
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              {items.map(renderCard)}
            </Box>
          </Box>
        );
      })}
    </PageLayout>
  );
}
